import { useEffect, useMemo, useRef } from "react";
import mapData from "lib/stores/mapData";
import ToolbarControl from "components/common/Toolbar/ToolbarControl";
import dictMap from "lib/helpers/dictMap";
import MobileLayersButton from "../../MobileLayersButton";
import context from "lib/stores/context";
import apiData from "lib/stores/apiData";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as Toggle from "@radix-ui/react-toggle";
import { RadixTooltip } from "components/common/Tooltip/RadixTooltip";
import TooltipButton from "components/common/Tooltip/TooltipButton";
import RadixSelect from "components/common/RadixSelect";
import ColorLegend from "./ColorLegend";
import ProductsControlBarMobile from "components/ProductsControlBar/Mobile";
import classNames from "classnames";
import { ReactComponent as IconLayers } from "components/icons/Layers.svg";
import { useSnapshot } from "valtio";
import styles from "./index.module.scss";
import commonStyles from "styles/common.module.scss";
import { useResizeWithinRange } from "lib/hooks/useResizeWithinRange";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@radix-ui/react-icons";
import { ReactComponent as IconCloseDrawer } from "components/icons/close-panel.svg";

const MapControlsToolbarsMobile = () => {
  const {
    showProductsMobile,
    showProducts,
    activeCategory,
    year,
    productsCountry,
  } = useSnapshot(mapData);
  const { isCollapsed } = useSnapshot(context);
  const { countries } = useSnapshot(apiData);

  const getPlaceholder = () => {
    try {
      if (countries[productsCountry]) {
        return `${countries[productsCountry].name} (${productsCountry})`;
      } else {
        return "Select Country";
      }
    } catch (error) {
      return "Select Country";
    }
  };

  const setSelectedOption = (value: string) => {
    mapData.productsCountry = value;
    mapData.selectedProduct = countries[value].imports[0].product.toLowerCase();
    context.activeCountry = value;
    // TODO: Centralise this logic, and more generally state change logic throughout
    context.activeRegion = apiData.countries[value].region_id;
  };

  const getCountryOptions = () => {
    return dictMap(countries, (country) => {
      if (!country?.imports?.length) return null;
      return {
        label: `${country.name}`,
        value: country.iso,
      };
    })
      .filter((c) => {
        if (c === null) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        return 1;
      });
  };

  if (!isCollapsed) {
    return (
      <div className={styles["sidepanel-collapse-button-mobile"]}>
        <div className="flex items-center justify-end flex-1">
          <button
            onClick={() => {
              context.isCollapsed = true;
              // context.activeTabWorldwide = -1;
              // context.activeTabCountries = -1;
              // context.activeTabRegions = -1;
            }}
            id="close-sidepanel-mobile"
            className="px-6 font-bold lg:text-base source md:text-sm text-xs  text-center flex items-center justify-center space-x-2"
          >
            <span className="inline-block">Close</span>
            <div className="icon-wrapper lg:w-[15px] md:w-[10px] w-[8px] font-normal">
              <IconCloseDrawer />
            </div>
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      id="toolbar-map-controls-mobile"
      className={classNames(
        styles["toolbar-map-controls-mobile"],
        !isCollapsed ? "pointer-events-none opacity-0" : "pointer-events-none",
      )}
    >
      <div
        className={classNames(
          styles["mobile-layers-button-container"],
          "w-full flex items-center justify-end relative md:pr-[20px] pr-[15px] lg:pb-[20px] md:pb-[15px] pb-[10px]",
        )}
      >
        <MobileLayersButton />
      </div>
      {/* Imports Control Bar */}
      {productsCountry && <ProductsControlBarMobile />}

      {/* Primary Controls Bar */}
      <div
        id="primary-controls-bar-mobile"
        className={styles["primary-controls-bar-mobile"]}
      >
        <div className="tooltip-wrapper">
          <RadixTooltip content="Tooltip" Trigger={TooltipButton} />
        </div>
        <span className={styles["details-bar"]}>
          <RadixSelect
            onValueChange={(
              category: "prevalence" | "vulnerability" | "gov-response",
            ) => {
              mapData.activeCategory = category;
            }}
            placeholder={{
              prevalence: "Prevalence",
              "gov-response": "Gov Response",
              vulnerability: "Vulnerability",
            }[activeCategory]}
            value={activeCategory}
            triggerClassName={classNames(
              styles["radix-select-mobile-map-controls"],
              "border-[1px] border-black pointer-events-auto",
            )}
            selectContentClassName="-translate-x-[2px] max-h-[800px] md:w-32 lg:w-48 xl:w-64"
            groups={[
              {
                label: "Select Dimension",
                items: [
                  {
                    label: "Prevalence",
                    value: "prevalence",
                  },
                  {
                    label: "Vulnerability",
                    value: "vulnerability",
                  },
                  {
                    label: "Government Response",
                    value: "gov-response",
                  },
                ],
              },
            ]}
          />
          {/* Import Country Selector */}
          <ToolbarControl
            className={classNames(
              styles.toolbarControl,
              "mr-[1em] pointer-events-auto",
            )}
            label="G20 Country"
          >
            <RadixSelect
              onValueChange={setSelectedOption}
              value={productsCountry}
              selectContentClassName="-translate-x-[2px] max-h-[800px] md:w-64 lg:w-48 xl:w-64"
              placeholder={getPlaceholder()}
              groups={[
                {
                  label: "G20 Countries",
                  items: getCountryOptions(),
                },
              ]}
            />
          </ToolbarControl>
        </span>

        {/* G20 Imports Toggle */}
        <ColorLegend />
      </div>
    </div>
  );
};

export default MapControlsToolbarsMobile;
